/* =============================================================
   CountryPage — United Dream Mongolia
   Design: "Authoritative Horizon" — per-country detail page
   Sections: Hero → Visa Types → Documents → Steps → FAQ → Form
   ============================================================= */
import { useParams, useLocation } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  FileText,
  ChevronDown,
  Send,
  Phone,
  MapPin,
  User,
  Globe,
  ShoppingCart,
  ArrowRight,
} from "lucide-react";
import { countries } from "@/lib/countryData";
import { toast } from "sonner";
import { useOrderModal } from "@/contexts/OrderModalContext";
import USASchoolsDirectory from "@/components/USASchoolsDirectory";
import IrelandSchoolsDirectory from "@/components/IrelandSchoolsDirectory";
import { useConsultation } from "@/contexts/ConsultationModalContext";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08 },
  }),
};

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span
          className="font-semibold text-[#0A1628] text-sm pr-4"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {q}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform duration-300 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="px-5 pb-4 text-slate-500 text-sm leading-relaxed border-t border-slate-100 pt-3">
              {a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Slug → primary visa key mapping for OrderModal
const SLUG_TO_VISA_KEY: Record<string, string> = {
  usa: "usa-travel",
  australia: "australia-travel",
  canada: "canada-travel",
  schengen: "schengen-travel",
  ireland: "ireland-study",
  czech: "czech-work",
};

export default function CountryPage() {
  const { openConsultation } = useConsultation();
  const params = useParams<{ slug: string }>();
  const [, navigate] = useLocation();
  const openOrder = useOrderModal();
  const country = countries[params.slug ?? ""];

  const [form, setForm] = useState({ name: "", phone: "", visaType: "" });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSchools, setShowSchools] = useState(true);

  if (!country) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF]">
        <div className="text-center">
          <div className="text-6xl mb-4">🌍</div>
          <h2 className="text-2xl font-bold text-[#0A1628] mb-2" style={{ fontFamily: "var(--font-display)" }}>
            Улс олдсонгүй
          </h2>
          <button
            onClick={() => navigate("/")}
            className="mt-4 px-6 py-3 rounded-xl bg-[#1E6FD9] text-white font-semibold"
          >
            Нүүр хуудас руу буцах
          </button>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.visaType) {
      toast.error("Бүх талбарыг бөглөнө үү");
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1200));
    setLoading(false);
    setSubmitted(true);
    toast.success("Таны хүсэлт амжилттай илгээгдлээ!");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFF]">
      {/* ── HERO ─────────────────────────────────────────────── */}
      <div className={`bg-gradient-to-br ${country.heroColor} relative overflow-hidden`}>
        {/* Grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
          }}
        />
        <div className="absolute top-1/3 left-1/4 w-80 h-80 rounded-full blur-3xl opacity-10"
          style={{ background: country.accentColor }} />

        <div className="container relative z-10 pt-8 pb-16">
          {/* Back button */}
          <motion.button
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-white/70 hover:text-white transition-colors mb-10 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Буцах</span>
          </motion.button>

          <div className="flex flex-col md:flex-row md:items-end gap-6 md:gap-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4 }}
              className="w-24 h-24 rounded-3xl bg-white/10 border border-white/20 backdrop-blur-sm flex items-center justify-center text-5xl shadow-xl flex-shrink-0"
            >
              {country.flag}
            </motion.div>
            <div>
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-white/60 text-sm font-medium tracking-widest uppercase mb-1"
              >
                {country.nameEn}
              </motion.div>
              <motion.h1
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight mb-2"
                style={{ fontFamily: "var(--font-display)" }}
              >
                {country.name} Виз
              </motion.h1>
              <motion.p
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-white/70 text-lg"
              >
                {country.tagline}
              </motion.p>
            </div>
          </div>
        </div>
      </div>

      {/* ── MAIN CONTENT ─────────────────────────────────────── */}
      <div className="container py-14">
        <div className="grid lg:grid-cols-[1fr_380px] gap-10 xl:gap-14 items-start">

          {/* LEFT COLUMN */}
          <div className="space-y-12">

            {/* Description */}
            <motion.p
              variants={fadeUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="text-slate-600 text-base leading-relaxed border-l-4 pl-5"
              style={{ borderColor: country.accentColor }}
            >
              {country.description}
            </motion.p>

            {/* ── VISA TYPES ─────────────────────────────────── */}
            <section>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-2xl font-extrabold text-[#0A1628] mb-6"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Визийн Төрлүүд
              </motion.h2>
              <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {country.visaTypes
                  .filter((v) => !(country.slug === "usa" && v.name.includes("B1/B2")))
                  .map((v, i) => {
                  const isUSAStudentVisa = country.slug === "usa" && v.name.includes("F-1");
                  return (
                    <motion.div
                      key={v.name}
                      custom={i}
                      variants={fadeUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      whileHover={{ y: -4 }}
                      className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all p-5 flex flex-col"
                    >
                      <div
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border mb-4 w-fit ${country.badgeColor}`}
                      >
                        <Globe className="w-3 h-3" />
                        {v.duration}
                      </div>
                      <h3
                        className="font-bold text-[#0A1628] text-sm mb-2"
                        style={{ fontFamily: "var(--font-display)" }}
                      >
                        {v.name}
                      </h3>
                      <p className="text-slate-500 text-xs leading-relaxed flex-1">{v.description}</p>
                      {isUSAStudentVisa && (
                        <div className="mt-3 flex items-center gap-1.5 text-xs font-semibold py-1.5 px-3 rounded-lg bg-blue-50 border border-blue-100" style={{ color: country.accentColor }}>
                          🏫 Доор сургуулийн жагсаалтыг харна уу
                        </div>
                      )}
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs text-slate-400">
                          <Clock className="w-3.5 h-3.5" />
                          {v.processingTime}
                        </div>
                        <div
                          className="text-sm font-bold"
                          style={{ color: country.accentColor }}
                        >
                          {v.price}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </section>

             {/* ── USA F-1 SCHOOL DIRECTORY ───────────────── */}
            {country.slug === "usa" && showSchools && (
              <motion.section
                variants={fadeUp}
                initial="hidden"
                animate="visible"
                exit="hidden"
              >
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="mb-6 overflow-hidden rounded-2xl border border-violet-200 bg-gradient-to-r from-violet-50 via-indigo-50 to-sky-50 p-6"
                >
                  <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
                    <div>
                      <div className="text-xs font-bold tracking-[0.15em] text-violet-700">EARLY COLLEGE 16+</div>
                      <div className="mt-1 text-lg font-black text-[#0A1628]">16 наснаас АНУ, Канадад Early College хөтөлбөрөөр суралцах боломж</div>
                      <div className="mt-1 text-sm text-slate-600">16-д эхэлж, 18-д диплом + Associate зэрэг авч, 20-д бакалаврын зэргээ авах хөтөлбөрийн мэдээлэл, сургуулийн төлбөр, байр, мэргэжил болон визийн бэлтгэлийг нэг хуудсаар харна.</div>
                    </div>
                    <a href="/early-college" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-violet-600 px-5 py-3 text-sm font-bold text-white shadow-md shadow-violet-200 transition hover:bg-violet-700">
                      Early College харах →
                    </a>
                  </div>
                </motion.div>
                {/* Cost Calculator CTA — above school list */}
                <motion.div
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="mb-6 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 rounded-2xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4"
                >
                  <div>
                    <div className="font-bold text-[#0A1628] text-lg mb-1">АНУ-д суралцах нийт зардлаа тооцоолох</div>
                    <div className="text-slate-600 text-sm">Сургалтын төрөл, гэр бүлийн бүрэлдэхүүнээ оруулаад нийт зардлаа USD болон ₮-ээр харна уу.</div>
                  </div>
                  <a
                    href="/us-cost-calculator"
                    className="flex-shrink-0 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-xl transition-colors shadow-md shadow-blue-200 whitespace-nowrap"
                  >
                    🎓 Зардал тооцоолох →
                  </a>
                </motion.div>
                <USASchoolsDirectory />
              </motion.section>
            )}

                   {/* ── CZECH JOB LISTINGS ────────────────── */}
            {country.slug === "czech" && (
              <motion.section
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <div className="mb-6 overflow-hidden rounded-3xl border border-indigo-200 bg-gradient-to-r from-[#0b2540] via-[#164d78] to-[#9e2330] p-6 text-white shadow-lg shadow-indigo-900/10 sm:p-7">
                  <div className="flex flex-col items-start justify-between gap-5 sm:flex-row sm:items-center">
                    <div className="max-w-xl">
                      <p className="text-xs font-black tracking-[0.13em] text-sky-200">ХЯЗГААРЛАГДМАЛ БҮРТГЭЛ</p>
                      <h2 className="mt-2 text-2xl font-black tracking-tight">Чех Улс руу ажиллах 10 нээлттэй байр</h2>
                      <p className="mt-2 text-sm leading-6 text-sky-50/85">6 эмэгтэй орон тоо, 2 гэр бүл хамт бүртгүүлэх боломжтой. Бүртгэл 10 сарын 5 хүртэл.</p>
                    </div>
                    <a href="/czech-jobs" className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-white px-5 py-3 text-sm font-black text-[#0b2540] transition hover:bg-sky-50">Дэлгэрэнгүй харах <ArrowRight className="h-4 w-4" /></a>
                  </div>
                </div>
              </motion.section>
            )}

            {/* ── IRELAND SCHOOLS DIRECTORY ────────────── */}
            {country.slug === "ireland" && (
              <motion.section
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                <IrelandSchoolsDirectory />
              </motion.section>
            )}

            {/* ── DOCUMENTS ──────────────────────────────────── */}
            <section>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-2xl font-extrabold text-[#0A1628] mb-6"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Шаардлагатай Баримт Бичгүүд
              </motion.h2>
              <motion.div
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6"
              >
                <div className="flex items-center gap-2.5 mb-5">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ background: `${country.accentColor}18` }}
                  >
                    <FileText className="w-5 h-5" style={{ color: country.accentColor }} />
                  </div>
                  <span className="font-semibold text-[#0A1628] text-sm" style={{ fontFamily: "var(--font-display)" }}>
                    Бүрэн баримт бичгийн жагсаалт
                  </span>
                </div>
                <ul className="grid sm:grid-cols-2 gap-2.5">
                  {country.documents.map((doc, i) => (
                    <motion.li
                      key={doc}
                      custom={i}
                      variants={fadeUp}
                      initial="hidden"
                      whileInView="visible"
                      viewport={{ once: true }}
                      className="flex items-start gap-2.5 text-sm text-slate-600"
                    >
                      <CheckCircle2
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                        style={{ color: country.accentColor }}
                      />
                      {doc}
                    </motion.li>
                  ))}
                </ul>

                {/* ── Smart info notes ── */}
                {country.slug !== "czech" && <div className="mt-5 space-y-3">
                  <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3.5">
                    <span className="text-lg mt-0.5">💡</span>
                    <div>
                      <p className="text-xs font-semibold text-amber-800 mb-0.5">Банкны дансны талаар — түгээмэл буруу ойлголт</p>
                      <p className="text-xs text-amber-700">"Их мөнгөтэй байвал виз гарна" — энэ нь үнэн биш. Элчин сайдын яам мөнгөний хэмжээнээс илүүтэй <strong>тогтвортой байдал</strong> ба <strong>эх үүсвэрийг</strong> хардаг. Гэнэт их мөнгө оруулах нь эсрэгээрээ сэжиг төрүүлдэг. Гэхдээ энэ нөхцөлд ч виз авсан хүмүүс олон байдаг.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3.5">
                    <span className="text-lg mt-0.5">💪</span>
                    <div>
                      <p className="text-xs font-semibold text-blue-800 mb-0.5">Ажилгүй байлаа виз авч болохгүй гэсэн үг биш</p>
                      <p className="text-xs text-blue-700">Элчин сайдын яам нийт дүрийг харж шийдвэр гаргадаг. Ажилгүй байсан ч гэр бүлийн орлого, хадгаламж, монголд үлдэх шалтгаан зэргээр баримтаар байвал виз авсан хүмүүс олон байдаг. Манай зөвлөхтэй ярилцаад таны нөхцөлд тохиромжтой стратегийг боловсруулъя.</p>
                    </div>
                  </div>
                </div>}

              </motion.div>
            </section>

            {/* ── STEPS ──────────────────────────────────────── */}
            <section>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-2xl font-extrabold text-[#0A1628] mb-6"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Процессын Алхамууд
              </motion.h2>
              <div className="space-y-4">
                {country.steps.map((step, i) => (
                  <motion.div
                    key={step.title}
                    custom={i}
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="flex gap-4 bg-white rounded-2xl border border-slate-100 shadow-sm p-5"
                  >
                    <div
                      className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-sm font-bold flex-shrink-0 shadow-md"
                      style={{ background: country.accentColor, fontFamily: "var(--font-display)" }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <div
                        className="font-bold text-[#0A1628] text-sm mb-1"
                        style={{ fontFamily: "var(--font-display)" }}
                      >
                        {step.title}
                      </div>
                      <div className="text-slate-500 text-sm leading-relaxed">{step.desc}</div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            {/* ── FAQ ────────────────────────────────────────── */}
            <section>
              <motion.h2
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="text-2xl font-extrabold text-[#0A1628] mb-6"
                style={{ fontFamily: "var(--font-display)" }}
              >
                Түгээмэл Асуултууд
              </motion.h2>
              <div className="space-y-3">
                {country.faqs.map((faq) => (
                  <FAQItem key={faq.q} q={faq.q} a={faq.a} />
                ))}
              </div>
            </section>
          </div>

          {/* RIGHT COLUMN — Sticky booking form */}
          <div className="lg:sticky lg:top-24">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/60 overflow-hidden"
            >
              {/* Form header */}
              <div
                className="p-6 text-white"
                style={{ background: `linear-gradient(135deg, #0A1628, ${country.accentColor}99)` }}
              >
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-3xl">{country.flag}</span>
                  <div>
                    <div
                      className="font-bold text-lg"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {country.name} Виз Авах
                    </div>
                    <div className="text-white/60 text-xs">Үнэгүй зөвлөгөө авах</div>
                  </div>
                </div>
              </div>

              {/* Form body */}
              <div className="p-6">
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-6"
                  >
                    <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                      <CheckCircle2 className="w-7 h-7 text-green-500" />
                    </div>
                    <h3
                      className="text-lg font-bold text-[#0A1628] mb-1"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      Амжилттай!
                    </h3>
                    <p className="text-slate-500 text-sm mb-5">
                      Манай зөвлөх 24 цагийн дотор холбогдоно.
                    </p>
                    <button
                      onClick={() => { setSubmitted(false); setForm({ name: "", phone: "", visaType: "" }); }}
                      className="text-sm font-medium hover:underline"
                      style={{ color: country.accentColor }}
                    >
                      Дахин илгээх
                    </button>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label className="block text-xs font-semibold text-[#0A1628] mb-1.5" style={{ fontFamily: "var(--font-display)" }}>
                        Нэр
                      </label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="Таны нэр"
                          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-[#0A1628] placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                          style={{ "--tw-ring-color": `${country.accentColor}50` } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#0A1628] mb-1.5" style={{ fontFamily: "var(--font-display)" }}>
                        Утасны дугаар
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                          type="tel"
                          value={form.phone}
                          onChange={(e) => setForm({ ...form, phone: e.target.value })}
                          placeholder="+976 XXXX XXXX"
                          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-[#0A1628] placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all"
                          style={{ "--tw-ring-color": `${country.accentColor}50` } as React.CSSProperties}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-[#0A1628] mb-1.5" style={{ fontFamily: "var(--font-display)" }}>
                        Визийн төрөл
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                        <select
                          value={form.visaType}
                          onChange={(e) => setForm({ ...form, visaType: e.target.value })}
                          className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-slate-200 bg-slate-50 text-[#0A1628] text-sm focus:outline-none focus:ring-2 focus:border-transparent transition-all appearance-none"
                          style={{ "--tw-ring-color": `${country.accentColor}50` } as React.CSSProperties}
                        >
                          <option value="" disabled>Визийн төрөл сонгох</option>
                          {country.visaTypes.map((v) => (
                            <option key={v.name} value={v.name}>{v.name}</option>
                          ))}
                        </select>
                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        </div>
                      </div>
                    </div>

                    <a
                      href="#" onClick={(e) => { e.preventDefault(); openConsultation(); }}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-semibold text-sm btn-shimmer transition-all shadow-md hover:-translate-y-0.5"
                      style={{
                        background: country.accentColor,
                        boxShadow: `0 4px 14px ${country.accentColor}40`,
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      <Send className="w-4 h-4" />
                      Зөвлөгөө авах хүсэлт илгээх
                    </a>

                    <div className="relative flex items-center gap-2 my-1">
                      <div className="flex-1 h-px bg-slate-100" />
                      <span className="text-[10px] text-slate-400 font-medium">эсвэл</span>
                      <div className="flex-1 h-px bg-slate-100" />
                    </div>
                    <button
                      type="button"
                      onClick={() => openOrder(SLUG_TO_VISA_KEY[country.slug] ?? "")}
                      className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-semibold text-sm transition-all shadow-md hover:-translate-y-0.5"
                      style={{
                        background: "linear-gradient(135deg, #1E6FD9, #3b82f6)",
                        boxShadow: "0 4px 14px rgba(30,111,217,0.35)",
                        fontFamily: "var(--font-display)",
                      }}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Виз мэдүүлэх
                    </button>
                    <p className="text-center text-xs text-slate-400">
                      Үнэгүй зөвлөгөө · 24 цагийн дотор холбогдоно
                    </p>
                  </form>
                )}
              </div>

              {/* Contact info */}
              <div className="px-6 pb-6 pt-0 space-y-2">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <Phone className="w-3.5 h-3.5 flex-shrink-0" style={{ color: country.accentColor }} />
                  {country.slug === "czech" ? "+976 9507-5552" : "+976 9900-0000"}
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color: country.accentColor }} />
                  {country.slug === "czech" ? "СУИС-ийн урд Gem Castle төв 9 давхарт 907 тоот" : "Улаанбаатар, Монгол"}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer strip */}
      <div className="bg-[#0A1628] py-6 mt-10">
        <div className="container flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#1E6FD9] flex items-center justify-center">
              <Globe className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-white text-sm" style={{ fontFamily: "var(--font-display)" }}>
              United Dream Mongolia
            </span>
          </div>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1.5 text-white/50 hover:text-white text-sm transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Нүүр хуудас руу буцах
          </button>
        </div>
      </div>
    </div>
  );
}
